"use client";

import Image from "next/image";
import Link from "next/link";
import React from "react";

export interface BoxContent {
  contentType?: "text" | "image";
  title?: string;
  description?: string;
  imageUrl?: string;
  imageAlt?: string;
  link?: string;
  linkText?: string;
}

export interface ProductShowcaseSectionProps {
  title?: string;
  subheading?: string;
  imageUrl?: string;
  imageAlt?: string;
  leftBox?: BoxContent;
  rightBox?: BoxContent;
  disclaimerText?: string;
}

export default function ProductShowcaseSection({
  title = "Advanced Hair Transplant Technology",
  subheading,
  imageUrl = "https://res.cloudinary.com/dkaghqnvm/image/upload/v1766235882/Trichos/Website/ugraft-zeus-wireless-dr-john-watts-trichos-india.png",
  imageAlt = "U-Graft Zeus Wireless - Dr. John Watts Trichos India",
  leftBox,
  rightBox,
  disclaimerText = "Image is for illustrative purposes only. Individual lab results may vary.",
}: ProductShowcaseSectionProps) {
  const leftBoxContent = leftBox || {
    contentType: "text" as const,
    title: "U-Graft Zeus Wireless",
    link: "#",
    linkText: "Learn more",
  };
  const rightBoxContent = rightBox || {
    contentType: "text" as const,
    title: "Advanced Technology",
    link: "#",
    linkText: "Learn more",
  };
  return (
    <section className="w-full py-8 md:py-12">
      <div
        className="mx-auto w-full max-w-screen-2xl rounded-3xl p-6 md:p-8 lg:p-12"
        style={{
          background: "linear-gradient(to top, #F9EAAD 0%, #CDDAE2 100%)",
        }}
      >
        {/* Title */}
        {title && (
          <div className="mb-0 text-center">
            <h2 className="text-2xl font-bold text-gray-900 md:text-3xl lg:text-4xl">
              {title}
            </h2>
            {subheading && (
              <p className="mt-2 text-base text-gray-600 md:text-lg">
                {subheading}
              </p>
            )}
          </div>
        )}

        {/* Hero Image */}
        <div className="relative mb-6 w-full overflow-hidden rounded-2xl">
          {/* Animated Circular Line with Sparkles */}
          <div className="absolute inset-0 z-0 flex items-center justify-center overflow-hidden">
            <svg
              className="h-full w-full"
              viewBox="0 0 1200 600"
              preserveAspectRatio="xMidYMid meet"
              style={{ position: "absolute", top: 0, left: 0 }}
            >
              <defs>
                <linearGradient
                  id="sparkle-gradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="transparent" />
                  <stop offset="50%" stopColor="#F7E9AE" />
                  <stop offset="100%" stopColor="transparent" />
                </linearGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              {/* Circular Arc Path */}
              <path
                id="circular-line"
                d="M 100 300 Q 300 100 600 300 T 1100 300"
                fill="none"
                stroke="url(#sparkle-gradient)"
                strokeWidth="2"
                strokeDasharray="1000"
                strokeDashoffset="1000"
                style={{
                  animation: "drawLine 30s ease-in-out infinite",
                }}
                filter="url(#glow)"
              />
              {/* Sparkles positioned along the path */}
              {[...Array(8)].map((_, i) => {
                const progress = i / 7; // 0 to 1
                // Calculate position along the quadratic bezier curve
                // Path: M 100 300 Q 300 100 600 300 T 1100 300
                let x, y;
                if (progress <= 0.5) {
                  // First half: Q 300 100 600 300
                  const t = progress * 2; // 0 to 1 for first curve
                  x =
                    (1 - t) * (1 - t) * 100 +
                    2 * (1 - t) * t * 300 +
                    t * t * 600;
                  y =
                    (1 - t) * (1 - t) * 300 +
                    2 * (1 - t) * t * 100 +
                    t * t * 300;
                } else {
                  // Second half: T 1100 300 (smooth continuation)
                  const t = (progress - 0.5) * 2; // 0 to 1 for second curve
                  const prevX = 600;
                  const prevY = 300;
                  const controlX = 2 * 600 - 300; // Reflection of previous control point
                  const controlY = 2 * 300 - 100;
                  x =
                    (1 - t) * (1 - t) * prevX +
                    2 * (1 - t) * t * controlX +
                    t * t * 1100;
                  y =
                    (1 - t) * (1 - t) * prevY +
                    2 * (1 - t) * t * controlY +
                    t * t * 300;
                }
                const sparkleDelay = i * 0.25;
                return (
                  <circle
                    key={i}
                    cx={x}
                    cy={y}
                    r="4"
                    fill="#F7E9AE"
                    opacity="0"
                    style={{
                      animation: `sparkleAppear 30s ease-in-out infinite ${sparkleDelay}s`,
                    }}
                    filter="url(#glow)"
                  />
                );
              })}
            </svg>
          </div>
          <div className="relative z-10 mx-auto -mt-4 w-full md:-mt-6 md:w-2/5">
            <Image
              src={imageUrl}
              alt={imageAlt}
              width={1200}
              height={600}
              className="h-auto w-full object-cover"
              priority
            />
          </div>
        </div>

        {/* Boxes Container - Below the image */}
        <div className="-mt-20 mb-6 mx-auto flex w-11/12 flex-col gap-4 md:-mt-24 md:flex-row md:gap-4">
          {/* Left Box */}
          <div
            className="flex flex-1 flex-col rounded-3xl p-6 shadow-lg md:aspect-[2/1] md:p-8"
            style={{
              background: "linear-gradient(135deg, #FFFAE8 0%, #FFF6D5 100%)",
            }}
          >
            {leftBoxContent.contentType === "image" &&
            leftBoxContent.imageUrl ? (
              <div className="mb-auto flex flex-1 items-center justify-center">
                <Image
                  src={leftBoxContent.imageUrl}
                  alt={
                    leftBoxContent.imageAlt ||
                    leftBoxContent.title ||
                    "Box image"
                  }
                  width={400}
                  height={200}
                  className="h-auto max-h-full w-auto max-w-full object-contain"
                />
              </div>
            ) : (
              <>
                <h3 className="mb-2 text-xl font-semibold text-gray-900 md:mb-0 md:text-2xl">
                  {leftBoxContent.title}
                </h3>
                {leftBoxContent.description && (
                  <div className="mb-4 flex-1 md:mb-0 md:flex md:items-center">
                    <p className="text-base text-gray-700 md:text-lg">
                      {leftBoxContent.description}
                    </p>
                  </div>
                )}
              </>
            )}
            {leftBoxContent.link && (
              <div className="mt-auto flex justify-center md:justify-end">
                <Link
                  href={leftBoxContent.link}
                  className="rounded-full bg-black px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-800 md:text-base"
                  style={{ color: "#F7E9AE" }}
                >
                  {leftBoxContent.linkText || "Learn more"}
                </Link>
              </div>
            )}
          </div>

          {/* Right Box */}
          <div
            className="flex flex-1 flex-col rounded-3xl p-6 shadow-lg md:aspect-[2/1] md:p-8"
            style={{
              background: "linear-gradient(135deg, #FFFAE8 0%, #FFF6D5 100%)",
            }}
          >
            {rightBoxContent.contentType === "image" &&
            rightBoxContent.imageUrl ? (
              <div className="mb-auto flex flex-1 items-center justify-center">
                <Image
                  src={rightBoxContent.imageUrl}
                  alt={
                    rightBoxContent.imageAlt ||
                    rightBoxContent.title ||
                    "Box image"
                  }
                  width={400}
                  height={200}
                  className="h-auto max-h-full w-auto max-w-full object-contain"
                />
              </div>
            ) : (
              <>
                <h3 className="mb-2 text-xl font-semibold text-gray-900 md:mb-0 md:text-2xl">
                  {rightBoxContent.title}
                </h3>
                {rightBoxContent.description && (
                  <div className="mb-4 flex-1 md:mb-0 md:flex md:items-center">
                    <p className="text-base text-gray-700 md:text-lg">
                      {rightBoxContent.description}
                    </p>
                  </div>
                )}
              </>
            )}
            {rightBoxContent.link && (
              <div className="mt-auto flex justify-center md:justify-end">
                <Link
                  href={rightBoxContent.link}
                  className="rounded-full bg-black px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-800 md:text-base"
                  style={{ color: "#F7E9AE" }}
                >
                  {rightBoxContent.linkText || "Learn more"}
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Footer Disclaimer */}
        <p className="text-center text-xs text-gray-500 md:text-sm">
          {disclaimerText}
        </p>
      </div>
    </section>
  );
}
